







































































import { SfButton, SfSearchBar, SvgImage } from '~/components';
import { computed, defineComponent, nextTick, onBeforeUnmount, onMounted, ref, useContext, useRoute, useRouter, watch } from '@nuxtjs/composition-api';
import { debounce } from 'lodash-es';
import { useInitialSearchData } from '~/components/Header/SearchBar/useInitialSearchData';
import { useWebsiteCode } from '~/composables/useWebsiteCode';
import { useSearchBarResults } from '~/components/Header/SearchBar/useSearchBarResults';
import { useSearchHistory } from '~/components/Header/SearchBar/useSearchHistory';

type BlurTarget = {
  blur?: () => void;
  $refs?: { input?: BlurTarget };
};

type SearchBarInstance = {
  $refs?: { searchInput?: BlurTarget };
};

type SearchSubmitResult = {
  hasError: boolean;
  hasNoResults?: boolean;
  redirectUrl?: string;
};

type TerminalSearchState = 'error' | 'empty' | null;

const SEARCH_TERM_STORAGE_KEY = 'searchTerm';

export default defineComponent({
  name: 'SearchBar',
  components: {
    SfSearchBar,
    SfButton,
    SvgImage,
  },
  props: {
    isSearchOpen: {
      type: Boolean,
      default: false,
    },
    productsItemsPerPage: {
      type: Number,
      default: 12,
    },
    minTermLen: {
      type: Number,
      default: 3,
    },
  },
  emits: ['set-is-open', 'set-search-results-products', 'set-search-results-categories', 'set-autocomplete-sts', 'set-initial-custom-products', 'set-initial-custom-title', 'set-initial-categories', 'set-initial-sts', 'set-search-history', 'search-term', 'search-input', 'search-total-results', 'set-search-loading', 'set-search-error'],
  setup(props, { emit }) {
    const { isBy } = useWebsiteCode();
    const term = ref('');
    const isClearButtonVisible = ref(false);
    const showClearButton = debounce(() => {
      isClearButtonVisible.value = true;
    }, 300);
    const route = useRoute();
    const router = useRouter();
    const { app } = useContext();
    const searchBarInput = ref<SearchBarInstance | null>(null);
    const activeSearchRequestId = ref(0);
    const activeSearchTerm = ref('');
    const lastCompletedSearchTerm = ref('');
    const lastSearchRedirectUrl = ref('');
    const lastTerminalSearchTerm = ref('');
    const lastTerminalSearchState = ref<TerminalSearchState>(null);
    const correction = ref('');
    const customQuery = (params: { query: string; queryVariables?: Record<string, unknown> }) => (
      app.context.$vsf.$magento.api.customQuery(params)
    );

    const getStoredSearchTerm = () => {
      if (typeof window === 'undefined') return '';

      try {
        return window.localStorage.getItem(SEARCH_TERM_STORAGE_KEY) || '';
      } catch (error) {
        console.error('Failed to read stored search term', error);
        return '';
      }
    };

    const persistSearchTerm = (value?: string) => {
      if (typeof window === 'undefined') return;

      try {
        if (value) {
          window.localStorage.setItem(SEARCH_TERM_STORAGE_KEY, value);
        } else {
          window.localStorage.removeItem(SEARCH_TERM_STORAGE_KEY);
        }
      } catch (error) {
        console.error('Failed to update stored search term', error);
      }
    };

    const getRouteSearchTerm = () => {
      const query = route.value.query || {};
      const queryTerm = Array.isArray(query.term) ? query.term[0] : query.term;

      return typeof queryTerm === 'string' ? queryTerm.trim() : '';
    };

    const restoreSearchTermFromRoute = () => {
      const routeTerm = getRouteSearchTerm();
      if (!routeTerm) return;

      term.value = routeTerm;
      correction.value = '';
      persistSearchTerm(routeTerm);
      emit('search-input', routeTerm);
      emit('search-term', routeTerm);
    };

    const suggestionSuffix = computed(() => {
      if (!term.value || !correction.value) return '';
      const termLower = term.value.toLowerCase();
      const correctionLower = correction.value.toLowerCase();
      if (correctionLower.startsWith(termLower)) {
        return correction.value.slice(term.value.length);
      }
      return '';
    });

    const setSearchLoading = (value: boolean) => {
      emit('set-search-loading', value);
    };

    const startSearchLoading = () => {
      activeSearchRequestId.value += 1;
      const currentId = activeSearchRequestId.value;
      setSearchLoading(true);
      return currentId;
    };

    const finishSearchLoading = (requestId: number) => {
      if (requestId === activeSearchRequestId.value) {
        setSearchLoading(false);
      }
    };

    const publishSearchResults = ({
      products,
      categories,
      sts,
      total,
    }: {
      products: unknown[] | null;
      categories: unknown[] | null;
      sts: unknown[] | null;
      total: number | null;
    }) => {
      emit('set-search-results-products', products);
      emit('set-search-results-categories', categories);
      emit('set-autocomplete-sts', sts);
      emit('search-total-results', total);
    };

    const clearSearchResults = () => publishSearchResults({
      products: null,
      categories: null,
      sts: null,
      total: null,
    });

    const {
      removeSearchHistoryTerm,
      syncSearchHistory,
      updateSearchHistory,
    } = useSearchHistory({
      hasStorageConsent: () => String(app.$cookies?.get('togas-cookie-consent')) === '1',
      onChange: (history) => emit('set-search-history', history),
    });

    const {
      fetchInitialData,
      hasInitialPayload,
      initialDataLoading,
    } = useInitialSearchData({
      customQuery,
      startLoading: startSearchLoading,
      finishLoading: finishSearchLoading,
      onChange: ({ customProducts, customTitle, categories, sts }) => {
        emit('set-initial-custom-products', customProducts);
        emit('set-initial-custom-title', customTitle);
        emit('set-initial-categories', categories);
        emit('set-initial-sts', sts);
      },
    });

    const { fetchSearchResults } = useSearchBarResults({
      customQuery,
    });

    const normalizeSearchTerm = (value?: string) => String(value || '').trim().toLocaleLowerCase();

    const clearTerminalSearchState = () => {
      lastTerminalSearchTerm.value = '';
      lastTerminalSearchState.value = null;
    };

    const loadResultsForStoredTerm = () => {
      const storedTerm = String(term.value || '').trim();
      if (!storedTerm || storedTerm.length < props.minTermLen) {
        return;
      }

      const normalizedTerm = normalizeSearchTerm(storedTerm);
      if (
        normalizedTerm === activeSearchTerm.value ||
        normalizedTerm === lastCompletedSearchTerm.value
      ) {
        return;
      }

      debouncedHandleSearch.cancel();
      rawHandleSearch(storedTerm);
    };

    const loadInitialDataIfNeeded = () => {
      const hasTerm = Boolean(term.value?.trim());

      if (!props.isSearchOpen || hasTerm || hasInitialPayload.value || initialDataLoading.value) return;

      performInitialSearch();
    };

    const blurSearchInput = () => {
      if (typeof document === 'undefined') return;

      const searchInput = searchBarInput.value?.$refs?.searchInput;
      const inputElement = searchInput?.$refs?.input || searchInput;

      if (inputElement && typeof inputElement.blur === 'function') {
        inputElement.blur();
      }

      const activeElement = document.activeElement as HTMLElement | null;
      if (activeElement?.matches('input, textarea')) {
        activeElement.blur();
      }
    };

    const navigateToSearchRedirect = async (redirectUrl: string) => {
      if (/^(https?:)?\/\//i.test(redirectUrl) && process.client) {
        window.location.assign(redirectUrl);
        return;
      }

      await router.push(redirectUrl);
    };

    onMounted(() => {
      syncSearchHistory();

      loadInitialDataIfNeeded();
    });

    onBeforeUnmount(() => {
      showClearButton.cancel();
      debouncedHandleSearch.cancel();
    });

    watch(() => props.isSearchOpen, (newVal) => {
      if (newVal) {
        restoreSearchTermFromRoute();
        syncSearchHistory();
        loadInitialDataIfNeeded();
        loadResultsForStoredTerm();
      }
    });

    watch(term, (value) => {
      showClearButton.cancel();

      if (!value.length) {
        isClearButtonVisible.value = false;
        return;
      }

      if (!isClearButtonVisible.value) {
        showClearButton();
      }
    });

    const showSearch = () => {
      emit('set-is-open', true);
    };

    const hideSearch = () => {
      if (props.isSearchOpen) {
        emit('set-is-open', false);
      }
    };

    const forceCloseSearch = () => {
      hideSearch();

      if (process.client) {
        window.dispatchEvent(new CustomEvent('togas-search-force-close'));
      }
    };

    const resetSearch = () => {
      if (!term.value) return;

      showClearButton.cancel();
      isClearButtonVisible.value = false;
      debouncedHandleSearch.cancel();
      activeSearchRequestId.value += 1;
      activeSearchTerm.value = '';
      lastCompletedSearchTerm.value = '';
      lastSearchRedirectUrl.value = '';
      clearTerminalSearchState();
      setSearchLoading(false);
      term.value = '';
      persistSearchTerm();
      correction.value = '';
      clearSearchResults();
      emit('set-initial-categories', null);
      emit('set-initial-sts', null);
      emit('set-initial-custom-products', null);
      emit('set-initial-custom-title', null);
      emit('search-term', null);
      if (props.isSearchOpen) {
        performInitialSearch();
      }
    };


    const handleInput = (value: string | Event) => {
      let rawValue = '';
      if (typeof value === 'string') {
        rawValue = value;
      } else {
        const eventTarget = value?.target as HTMLInputElement | null;
        rawValue = typeof eventTarget?.value === 'string' ? eventTarget.value : '';
      }

      if (normalizeSearchTerm(rawValue) !== lastTerminalSearchTerm.value) {
        clearTerminalSearchState();
      }

      term.value = rawValue;
      emit('search-input', rawValue);
      emit('set-search-error', false);
      activeSearchRequestId.value += 1;

      if (rawValue.trim()) {
        setSearchLoading(true);
        debouncedHandleSearch(rawValue);
      } else {
        debouncedHandleSearch.cancel();
        correction.value = '';
        setSearchLoading(false);
      }
    };

    const rawHandleSearch = async (searchTerm: string): Promise<SearchSubmitResult> => {
      const searchRequestId = startSearchLoading();
      const normalizedSearchTerm = normalizeSearchTerm(searchTerm);
      activeSearchTerm.value = normalizedSearchTerm;
      term.value = searchTerm;
      emit('set-search-error', false);

      persistSearchTerm(searchTerm);

      emit('search-term', term.value);

      if (term.value.length < props.minTermLen) {
        activeSearchTerm.value = '';
        lastCompletedSearchTerm.value = '';
        lastSearchRedirectUrl.value = '';
        clearTerminalSearchState();
        clearSearchResults();
        await fetchInitialData();
        finishSearchLoading(searchRequestId);
        return { hasError: false };
      }

      try {
        const results = await fetchSearchResults(term.value, props.productsItemsPerPage);

        if (searchRequestId !== activeSearchRequestId.value) return { hasError: true };

        correction.value = results.correction;
        lastCompletedSearchTerm.value = normalizedSearchTerm;
        lastTerminalSearchTerm.value = normalizedSearchTerm;
        lastTerminalSearchState.value = results.products.length ? null : 'empty';
        lastSearchRedirectUrl.value = results.redirectUrl?.trim() || '';

        publishSearchResults({
          products: results.products.slice(0, 4),
          categories: results.categories,
          sts: results.sts,
          total: results.total,
        });
        emit('search-term', term.value);
        if (!isBy.value && !results.products.length && !lastSearchRedirectUrl.value) {
          await fetchInitialData();
        }

        return {
          hasError: false,
          hasNoResults: !results.products.length,
          redirectUrl: lastSearchRedirectUrl.value,
        };
      } catch (error) {
        if (searchRequestId !== activeSearchRequestId.value) return { hasError: true };

        console.error('Error in SearchBar.vue:', error);
        lastCompletedSearchTerm.value = '';
        lastSearchRedirectUrl.value = '';
        lastTerminalSearchTerm.value = normalizedSearchTerm;
        lastTerminalSearchState.value = 'error';
        correction.value = '';
        publishSearchResults({
          products: [],
          categories: [],
          sts: [],
          total: 0,
        });
        emit('set-search-error', true);
        return { hasError: true };
      } finally {
        if (activeSearchTerm.value === normalizedSearchTerm) {
          activeSearchTerm.value = '';
        }
        finishSearchLoading(searchRequestId);
      }
    };

    const debouncedHandleSearch = debounce(rawHandleSearch, 1000);

    const clearSearchInput = () => {
      showClearButton.cancel();
      debouncedHandleSearch.cancel();
      activeSearchRequestId.value += 1;
      activeSearchTerm.value = '';
      lastCompletedSearchTerm.value = '';
      lastSearchRedirectUrl.value = '';
      clearTerminalSearchState();
      isClearButtonVisible.value = false;
      term.value = '';
      correction.value = '';
      setSearchLoading(false);
      emit('search-input', '');
      emit('search-term', '');

      persistSearchTerm();
    };

    const searchFromHistory = (value: string) => {
      const historyTerm = String(value || '').trim();
      if (!historyTerm) return;

      debouncedHandleSearch.cancel();
      correction.value = '';
      term.value = historyTerm;
      emit('search-input', historyTerm);
      emit('set-search-error', false);
      rawHandleSearch(historyTerm);
    };

    async function performInitialSearch() {
      term.value = '';
      persistSearchTerm();
      correction.value = '';
      emit('set-search-error', false);
      emit('search-term', '');
      clearSearchResults();
      emit('set-initial-categories', null);
      emit('set-initial-sts', null);
      await fetchInitialData();
    }

    const handleKeydownEnter = async (searchTerm: string) => {
      debouncedHandleSearch.cancel();

      const submittedTerm = String(searchTerm || '').trim();
      if (!submittedTerm) return;

      term.value = submittedTerm;
      persistSearchTerm(submittedTerm);
      updateSearchHistory(submittedTerm);

      const normalizedSearchTerm = normalizeSearchTerm(submittedTerm);
      const hasCachedResult = normalizedSearchTerm === lastCompletedSearchTerm.value;
      const hasCachedError = normalizedSearchTerm === lastTerminalSearchTerm.value
        && lastTerminalSearchState.value === 'error';
      let result: SearchSubmitResult;

      if (hasCachedResult || hasCachedError) {
        activeSearchRequestId.value += 1;
        activeSearchTerm.value = '';
        setSearchLoading(false);
        result = {
          hasError: hasCachedError,
          hasNoResults: lastTerminalSearchState.value === 'empty',
          redirectUrl: hasCachedResult ? lastSearchRedirectUrl.value : '',
        };
      } else {
        result = await rawHandleSearch(submittedTerm);
      }

      if (result.redirectUrl) {
        await navigateToSearchRedirect(result.redirectUrl);
        return;
      }

      if (result.hasError || result.hasNoResults) return;

      await router.push(app.localeRoute({ name: 'search', query: { term: submittedTerm } }));

      await nextTick();
      blurSearchInput();
    };

    watch(() => route.value.fullPath, async (_, previousFullPath) => {
      if (previousFullPath !== undefined) {
        hideSearch();
      }
      const query = route.value.query || {};
      const isSearchPage = 'term' in query;

      if (!isSearchPage) {
        debouncedHandleSearch.cancel();
        activeSearchRequestId.value += 1;
        activeSearchTerm.value = '';
        lastCompletedSearchTerm.value = '';
        lastSearchRedirectUrl.value = '';
        clearTerminalSearchState();
        correction.value = '';
        setSearchLoading(false);
      }

      term.value = isSearchPage
        ? getRouteSearchTerm() || getStoredSearchTerm()
        : '';
      await nextTick();
      blurSearchInput();
    }, { immediate: true });

    return {
      forceCloseSearch,
      showSearch,
      hideSearch,
      resetSearch,
      addSearchHistoryTerm: updateSearchHistory,
      handleKeydownEnter,
      term,
      removeSearchHistoryTerm,
      clearSearchInput,
      searchFromHistory,
      suggestionSuffix,
      handleInput,
      isClearButtonVisible,
    };
  },
});

